import React, { useEffect, useState } from "react"
import { Alert, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap"

import { uploadImage } from "../../../FIREBASECONFIG"
import {
  useResortsContentQuery,
  useUpdateResortsContentMutation,
} from "../../../api/resorts-content/resorts-content.hooks"
import { optimizeImageToWebP } from "../../../utils/imageOptimize"

const DEFAULT_RESORTS_CONTENT = {
  pageName: "resorts",
  type: "page",
  title: "Resorts",
  meta: {
    title: "TDCP Resorts",
    description:
      "Browse TDCP resorts, compare stays, and find quick answers to common booking questions.",
  },
  content: {
    heroSection: {
      isVisible: true,
      backgroundImage: "/assets/images/heroimg.png",
      heading: "Discover Pakistan",
      subheading: "Every View Tells A Story",
    },
    resortsSection: {
      isVisible: true,
      heading: "Discover popular TDCP resorts",
      subheading:
        "Stay at TDCP properties across Punjab- comfortable rooms, trusted hospitality, and easy online booking through our resort portal.",
    },
    faqsSection: {
      isVisible: true,
      heading: "Got questions?",
      subheading: "We've got answers!",
      items: [
        {
          question: "How do I book a room at a TDCP resort?",
          answer:
            "Open the TDCP resort portal, choose your property and dates, then complete guest details and payment. You can also reach the resort's front desk for walk-in availability, subject to rooms on hand.",
        },
        {
          question: "What is usually included with my stay?",
          answer:
            "Typical stays include lodging and standard services advertised for that property. On-site facilities such as dining, parking, or recreation may vary by location - check the resort listing or confirmation for specifics.",
        },
        {
          question: "Can I change or cancel my reservation?",
          answer:
            "Changes and cancellations follow the policy shown at booking time and on your confirmation. For help, use the contact options on the resort portal or call the property directly as early as possible before check-in.",
        },
        {
          question: "How do I get directions or help during my visit?",
          answer:
            "Each resort listing includes address and contact details. For travel within Punjab, TDCP's tourist information channels can also guide you on routes, local attractions, and transport options.",
        },
      ],
    },
    exploreSection: {
      isVisible: true,
      heading: "Ready to explore the",
      headingLineTwo: "heart of Punjab?",
      description:
        "Plan your next trip with TDCP - resorts, transport, and curated experiences across Punjab's cities and heritage sites.",
      contact: {
        label: "Contact us",
        phone: "051 3357066",
        email: "info@tdcp.gop.pk",
      },
      officeAddress: {
        label: "Office address",
        lines: [
          "68, Trade Centre Commercial Area,",
          "Block M.A Johar Town, Lahore, Punjab",
        ],
      },
      imagery: {
        left: "/assets/images/journey1.jpg",
        right: "/assets/images/journey3.jpg",
      },
    },
  },
}

const createDefaultContent = () => JSON.parse(JSON.stringify(DEFAULT_RESORTS_CONTENT))

const normalizeContent = (content) => {
  const next = createDefaultContent()

  if (!content) {
    return next
  }

  return {
    ...next,
    ...content,
    meta: {
      ...next.meta,
      ...(content.meta || {}),
    },
    content: {
      ...next.content,
      ...(content.content || {}),
      heroSection: {
        ...next.content.heroSection,
        ...(content.content?.heroSection || {}),
      },
      resortsSection: {
        ...next.content.resortsSection,
        ...(content.content?.resortsSection || {}),
      },
      faqsSection: {
        ...next.content.faqsSection,
        ...(content.content?.faqsSection || {}),
        items: Array.isArray(content.content?.faqsSection?.items)
          ? content.content.faqsSection.items.map((item) => ({
              question: "",
              answer: "",
              ...item,
            }))
          : next.content.faqsSection.items,
      },
      exploreSection: {
        ...next.content.exploreSection,
        ...(content.content?.exploreSection || {}),
        contact: {
          ...next.content.exploreSection.contact,
          ...(content.content?.exploreSection?.contact || {}),
        },
        officeAddress: {
          ...next.content.exploreSection.officeAddress,
          ...(content.content?.exploreSection?.officeAddress || {}),
          lines: Array.isArray(content.content?.exploreSection?.officeAddress?.lines)
            ? content.content.exploreSection.officeAddress.lines
            : next.content.exploreSection.officeAddress.lines,
        },
        imagery: {
          ...next.content.exploreSection.imagery,
          ...(content.content?.exploreSection?.imagery || {}),
        },
      },
    },
  }
}

const setNestedValue = (target, path, value) => {
  const [currentKey, ...restPath] = path

  if (!currentKey) {
    return target
  }

  if (restPath.length === 0) {
    target[currentKey] = value
    return target
  }

  if (typeof target[currentKey] !== "object" || target[currentKey] === null) {
    target[currentKey] = {}
  }

  setNestedValue(target[currentKey], restPath, value)
  return target
}

const ResortsContent = () => {
  const { content, isLoading, isError, error, contentId } = useResortsContentQuery()
  const { mutateAsync: updateContent, isLoading: isSaving } = useUpdateResortsContentMutation()
  const [formData, setFormData] = useState(createDefaultContent())
  const [alert, setAlert] = useState({ show: false, message: "", variant: "info" })
  const [isUploading, setIsUploading] = useState(false)

  useEffect(() => {
    setFormData(normalizeContent(content))
  }, [content])

  const canSave = Boolean(contentId) && !isSaving && !isUploading

  const showAlert = (message, variant = "info") => {
    setAlert({ show: true, message, variant })
  }

  const updateField = (path, value) => {
    setFormData((prev) => {
      const next = JSON.parse(JSON.stringify(prev))
      setNestedValue(next, path, value)
      return next
    })
  }

  const updateFaqItem = (index, field, value) => {
    setFormData((prev) => {
      const next = JSON.parse(JSON.stringify(prev))
      next.content.faqsSection.items[index] = {
        ...(next.content.faqsSection.items[index] || {}),
        [field]: value,
      }
      return next
    })
  }

  const addFaqItem = () => {
    setFormData((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        faqsSection: {
          ...prev.content.faqsSection,
          items: [...prev.content.faqsSection.items, { question: "", answer: "" }],
        },
      },
    }))
  }

  const removeFaqItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        faqsSection: {
          ...prev.content.faqsSection,
          items: prev.content.faqsSection.items.filter((_, itemIndex) => itemIndex !== index),
        },
      },
    }))
  }

  const uploadImageToField = async (path, file) => {
    if (!file) {
      return
    }

    try {
      setIsUploading(true)
      const optimizedFile = await optimizeImageToWebP(file, {
        quality: 0.75,
        maxWidth: 1600,
        maxHeight: 1600,
      })
      const url = await uploadImage(optimizedFile, "resorts")
      updateField(path, url)
      showAlert("Image uploaded successfully.", "success")
    } catch (uploadError) {
      console.error("Failed to upload image:", uploadError)
      showAlert("Failed to upload image.", "danger")
    } finally {
      setIsUploading(false)
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!contentId) {
      showAlert("No resorts content document was found in the contents collection.", "warning")
      return
    }

    try {
      await updateContent({
        id: contentId,
        data: formData,
      })
      showAlert("Resorts content updated successfully.", "success")
    } catch (saveError) {
      console.error("Failed to update resorts content:", saveError)
      showAlert("Failed to update resorts content.", "danger")
    }
  }

  const renderImageField = (label, value, path) => (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <div className="d-flex flex-column gap-2">
        {value ? (
          <div className="border rounded p-2 bg-white">
            <img
              src={value}
              alt={label}
              style={{ width: "100%", maxWidth: "320px", height: "180px", objectFit: "cover", borderRadius: "8px" }}
            />
          </div>
        ) : null}
        <Form.Control
          type="file"
          accept="image/*"
          onChange={(event) => uploadImageToField(path, event.target.files?.[0])}
          disabled={isUploading}
        />
        <Form.Text>
          The selected image is uploaded to Firebase Storage first, then the URL is saved in this field.
        </Form.Text>
      </div>
    </Form.Group>
  )

  if (isLoading) {
    return (
      <div className="col-12">
        <Card className="p-5 text-center">
          <Spinner animation="border" role="status" />
          <div className="mt-3">Loading resorts content...</div>
        </Card>
      </div>
    )
  }

  return (
    <div className="col-12">
      {alert.show ? (
        <Alert
          variant={alert.variant}
          dismissible
          onClose={() => setAlert({ show: false, message: "", variant: "info" })}
          className="mb-3"
        >
          {alert.message}
        </Alert>
      ) : null}

      {isError ? (
        <Alert variant="danger" className="mb-3">
          {error?.message || "Failed to load resorts content."}
        </Alert>
      ) : null}

      {!contentId ? (
        <Alert variant="warning" className="mb-3">
          No resorts content document was found in the <strong>contents</strong> collection. This page only updates an existing record.
        </Alert>
      ) : null}

      <Card className="shadow-sm">
        <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <h4 className="mb-1">Resorts CMS</h4>
          </div>
          <Button variant="primary" onClick={handleSubmit} disabled={!canSave}>
            {isSaving ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Card className="mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Hero Section</h5>
                  <Form.Check
                    type="checkbox"
                    label="Visible"
                    checked={Boolean(formData.content.heroSection.isVisible)}
                    onChange={(event) => updateField(["content", "heroSection", "isVisible"], event.target.checked)}
                  />
                </div>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Heading</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.content.heroSection.heading}
                        onChange={(event) => updateField(["content", "heroSection", "heading"], event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Subheading</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={formData.content.heroSection.subheading}
                        onChange={(event) => updateField(["content", "heroSection", "subheading"], event.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    {renderImageField(
                      "Background Image",
                      formData.content.heroSection.backgroundImage,
                      ["content", "heroSection", "backgroundImage"]
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Resorts Section</h5>
                  <Form.Check
                    type="checkbox"
                    label="Visible"
                    checked={Boolean(formData.content.resortsSection.isVisible)}
                    onChange={(event) => updateField(["content", "resortsSection", "isVisible"], event.target.checked)}
                  />
                </div>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Heading</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.content.resortsSection.heading}
                        onChange={(event) => updateField(["content", "resortsSection", "heading"], event.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Subheading</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={formData.content.resortsSection.subheading}
                        onChange={(event) => updateField(["content", "resortsSection", "subheading"], event.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">FAQs Section</h5>
                  <Form.Check
                    type="checkbox"
                    label="Visible"
                    checked={Boolean(formData.content.faqsSection.isVisible)}
                    onChange={(event) => updateField(["content", "faqsSection", "isVisible"], event.target.checked)}
                  />
                </div>
                <Row className="g-3 mb-3">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Heading</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.content.faqsSection.heading}
                        onChange={(event) => updateField(["content", "faqsSection", "heading"], event.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Subheading</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.content.faqsSection.subheading}
                        onChange={(event) => updateField(["content", "faqsSection", "subheading"], event.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {formData.content.faqsSection.items.map((item, index) => (
                  <Card key={`${item.question || "faq"}-${index}`} className="mb-3 border">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <strong>FAQ {index + 1}</strong>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeFaqItem(index)}
                          disabled={formData.content.faqsSection.items.length === 1}
                        >
                          Remove
                        </Button>
                      </div>
                      <Form.Group className="mb-3">
                        <Form.Label>Question</Form.Label>
                        <Form.Control
                          type="text"
                          value={item.question}
                          onChange={(event) => updateFaqItem(index, "question", event.target.value)}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Answer</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={item.answer}
                          onChange={(event) => updateFaqItem(index, "answer", event.target.value)}
                        />
                      </Form.Group>
                    </Card.Body>
                  </Card>
                ))}

                <Button variant="outline-primary" onClick={addFaqItem}>
                  Add FAQ
                </Button>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Explore Section</h5>
                  <Form.Check
                    type="checkbox"
                    label="Visible"
                    checked={Boolean(formData.content.exploreSection.isVisible)}
                    onChange={(event) => updateField(["content", "exploreSection", "isVisible"], event.target.checked)}
                  />
                </div>
                <Row className="g-3 mb-3">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Heading</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.content.exploreSection.heading}
                        onChange={(event) => updateField(["content", "exploreSection", "heading"], event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Heading Line Two</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.content.exploreSection.headingLineTwo}
                        onChange={(event) => updateField(["content", "exploreSection", "headingLineTwo"], event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={formData.content.exploreSection.description}
                        onChange={(event) => updateField(["content", "exploreSection", "description"], event.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Card className="mb-3 border-0 bg-light">
                      <Card.Body>
                        <h6 className="mb-3">Contact</h6>
                        <Form.Group className="mb-3">
                          <Form.Label>Label</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.content.exploreSection.contact.label}
                            onChange={(event) => updateField(["content", "exploreSection", "contact", "label"], event.target.value)}
                          />
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label>Phone</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.content.exploreSection.contact.phone}
                            onChange={(event) => updateField(["content", "exploreSection", "contact", "phone"], event.target.value)}
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>Email</Form.Label>
                          <Form.Control
                            type="email"
                            value={formData.content.exploreSection.contact.email}
                            onChange={(event) => updateField(["content", "exploreSection", "contact", "email"], event.target.value)}
                          />
                        </Form.Group>
                      </Card.Body>
                    </Card>
                    <Card className="mb-3 border-0 bg-light">
                      <Card.Body>
                        <h6 className="mb-3">Office Address</h6>
                        <Form.Group className="mb-3">
                          <Form.Label>Label</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.content.exploreSection.officeAddress.label}
                            onChange={(event) => updateField(["content", "exploreSection", "officeAddress", "label"], event.target.value)}
                          />
                        </Form.Group>
                        {formData.content.exploreSection.officeAddress.lines.map((line, index) => (
                          <Form.Group className="mb-3" key={`office-line-${index}`}>
                            <Form.Label>Line {index + 1}</Form.Label>
                            <Form.Control
                              type="text"
                              value={line}
                              onChange={(event) => updateField(["content", "exploreSection", "officeAddress", "lines", index], event.target.value)}
                            />
                          </Form.Group>
                        ))}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Row className="g-3">
                  <Col md={6}>
                    {renderImageField(
                      "Left Image",
                      formData.content.exploreSection.imagery.left,
                      ["content", "exploreSection", "imagery", "left"]
                    )}
                  </Col>
                  <Col md={6}>
                    {renderImageField(
                      "Right Image",
                      formData.content.exploreSection.imagery.right,
                      ["content", "exploreSection", "imagery", "right"]
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Body>
                <h5 className="mb-3">Meta</h5>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Meta Title</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.meta.title}
                        onChange={(event) => updateField(["meta", "title"], event.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Meta Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={formData.meta.description}
                        onChange={(event) => updateField(["meta", "description"], event.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <div className="d-flex justify-content-end gap-2">
              <Button type="submit" variant="primary" disabled={!canSave}>
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  )
}

export default ResortsContent